import React from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Avatar,
  Skeleton,
  SkeletonCircle,
} from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import { StoryGroup } from '../../services/storyService';

interface StoriesBarProps {
  storyGroups: StoryGroup[];
  currentUserId: string;
  loading?: boolean;
  onStoryClick: (group: StoryGroup, index: number) => void;
  onAddStoryClick?: () => void;
}

const StoriesBar: React.FC<StoriesBarProps> = ({
  storyGroups,
  currentUserId,
  loading = false,
  onStoryClick,
  onAddStoryClick,
}) => {
  const myStories = storyGroups.find(g => g.odD === currentUserId);
  const otherStories = storyGroups.filter(g => g.odD !== currentUserId);

  if (loading) {
    return (
      <Box
        py={3}
        px={4}
        bg={POKER_COLORS.darkBase}
        borderBottom="1px solid"
        borderColor="gray.800"
        overflowX="auto"
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <HStack spacing={4}>
          {[1, 2, 3, 4, 5].map((i) => (
            <VStack key={i} spacing={1} minW="70px">
              <SkeletonCircle size="62px" />
              <Skeleton height="10px" width="50px" />
            </VStack>
          ))}
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      py={3}
      px={4}
      bg={POKER_COLORS.darkBase}
      borderBottom="1px solid"
      borderColor="gray.800"
      overflowX="auto"
      css={{
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <HStack spacing={4} minW="max-content">
        {/* Your Story */}
        <VStack
          spacing={1}
          cursor="pointer"
          onClick={() => {
            if (myStories) {
              const idx = storyGroups.findIndex(g => g.odD === currentUserId);
              onStoryClick(myStories, idx);
            } else if (onAddStoryClick) {
              onAddStoryClick();
            }
          }}
          _active={{ transform: 'scale(0.95)' }}
          transition="transform 0.1s"
          minW="70px"
        >
          <Box position="relative">
            {myStories ? (
              <Box
                p="3px"
                borderRadius="full"
                bg={myStories.hasUnwatched 
                  ? `linear-gradient(45deg, ${POKER_COLORS.gold}, #f97316, #ec4899, ${POKER_COLORS.cyan})`
                  : 'gray.600'
                }
              >
                <Avatar
                  size="md"
                  name={myStories.username}
                  src={myStories.avatarUrl}
                  border="3px solid"
                  borderColor={POKER_COLORS.darkBase}
                />
              </Box>
            ) : (
              <Box
                w="62px"
                h="62px"
                borderRadius="full"
                bg="gray.800"
                border="2px dashed"
                borderColor="gray.600"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Plus size={28} color={POKER_COLORS.cyan} />
              </Box>
            )}
            
            {myStories && (
              <Box
                position="absolute"
                bottom="-2px"
                right="-2px"
                w="22px"
                h="22px"
                borderRadius="full"
                bg={POKER_COLORS.cyan}
                border="3px solid"
                borderColor={POKER_COLORS.darkBase}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Plus size={12} color="white" strokeWidth={3} />
              </Box>
            )}
          </Box>
          <Text 
            fontSize="xs" 
            color="white" 
            textAlign="center"
            maxW="70px"
            isTruncated
            fontWeight="medium"
          >
            Your Story
          </Text>
        </VStack>

        {/* Friends' Stories */}
        {otherStories.map((group) => {
          const groupIndex = storyGroups.findIndex(g => g.odD === group.odD);
          return (
            <VStack
              key={group.odD}
              spacing={1}
              cursor="pointer"
              onClick={() => onStoryClick(group, groupIndex)}
              _active={{ transform: 'scale(0.95)' }}
              transition="transform 0.1s"
              minW="70px"
            >
              <Box
                p="3px"
                borderRadius="full"
                bg={group.hasUnwatched 
                  ? `linear-gradient(45deg, ${POKER_COLORS.gold}, #f97316, #ec4899, ${POKER_COLORS.cyan})`
                  : 'gray.600'
                }
                boxShadow={group.hasUnwatched ? '0 0 15px rgba(249, 115, 22, 0.5)' : 'none'}
              >
                <Avatar
                  size="md"
                  name={group.username}
                  src={group.avatarUrl}
                  border="3px solid"
                  borderColor={POKER_COLORS.darkBase}
                />
              </Box>
              <Text 
                fontSize="xs" 
                color={group.hasUnwatched ? 'white' : 'gray.500'}
                textAlign="center"
                maxW="70px"
                isTruncated
                fontWeight={group.hasUnwatched ? 'semibold' : 'normal'}
              >
                {group.username}
              </Text>
            </VStack>
          );
        })}

        {/* Empty State */}
        {storyGroups.length === 0 && !loading && (
          <VStack py={2} px={4}>
            <Text color="gray.500" fontSize="sm">
              No stories yet 

            </Text>
            <Text color="gray.600" fontSize="xs">
              Win big and share!
            </Text>
          </VStack>
        )}
      </HStack>
    </Box>
  );
};

export default StoriesBar;